import { ArrowSquareOut, BookOpenText, CaretDown, CaretUp, CheckCircle, Circle, DotsSixVertical, Plus, Target, Trash } from '@phosphor-icons/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useOutletContext } from 'react-router-dom'
import type { DashboardOutletContext } from '../components/AppShell'
import { Button, CategoryBadge, ConflictDialog, Drawer, Field, Panel } from '../components/common'
import { api, conflictCurrent } from '../lib/api'
import { workingItemOwners } from '../lib/constants'
import { sortOrderBefore } from '../lib/sort'
import type { DashboardData, KnowledgePurpose, KnowledgeTopic, WorkingLink } from '../lib/types'

type TopicForm = Pick<KnowledgeTopic, 'category' | 'groupName' | 'topic' | 'done' | 'reporter' | 'reportDate'> & {
  links: WorkingLink[]
}
const defaultCategory = 'ESS 架構'
const emptyTopic: TopicForm = { category: defaultCategory, groupName: '', topic: '', done: false, reporter: '', reportDate: '', links: [] }
const topicLabels = { category: '類別', groupName: '群組', topic: '主題', done: '完成', reporter: '報告人', reportDate: '報告日期', links: '參考連結' }

export function KnowledgePage() {
  const { data, refresh, canEdit, isAdmin } = useOutletContext<DashboardOutletContext>()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [editing, setEditing] = useState<KnowledgeTopic | null>(null)
  const [form, setForm] = useState<TopicForm>(emptyTopic)
  const [open, setOpen] = useState(false)
  const [conflict, setConflict] = useState<KnowledgeTopic | null>(null)
  const [purposeEdit, setPurposeEdit] = useState<KnowledgePurpose | null>(null)
  const [purposeText, setPurposeText] = useState('')
  const [purposeOpen, setPurposeOpen] = useState(false)
  const [purposeExpanded, setPurposeExpanded] = useState(false)
  const [message, setMessage] = useState('')
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dragOverTopicId, setDragOverTopicId] = useState<string | null>(null)
  const [dragOverGroup, setDragOverGroup] = useState<string | null>(null)

  useEffect(() => {
    if (!message) return
    const timer = setTimeout(() => setMessage(''), 2500)
    return () => clearTimeout(timer)
  }, [message])

  const topics = useMemo(
    () => data.knowledge.topics.filter((row) =>
      `${row.category} ${row.groupName} ${row.topic} ${row.reporter}`.toLowerCase().includes(query.toLowerCase()),
    ),
    [data.knowledge.topics, query],
  )
  const groups = useMemo(() => [...new Set(topics.map((row) => row.category))], [topics])

  const save = useMutation({
    mutationFn: () => editing
      ? api.updateEntity<KnowledgeTopic>('knowledge-topics', editing.id, editing.version, form)
      : api.createEntity<KnowledgeTopic>('knowledge-topics', form),
    onSuccess: async (savedTopic) => {
      close()
      setMessage(`Topic「${savedTopic.topic}」已儲存。`)
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        if (editing) {
          return {
            ...old,
            knowledge: {
              ...old.knowledge,
              topics: old.knowledge.topics.map((t) => (t.id === savedTopic.id ? savedTopic : t)),
            },
          }
        }
        return {
          ...old,
          knowledge: {
            ...old.knowledge,
            topics: [...old.knowledge.topics, savedTopic],
          },
        }
      })
      await refresh()
    },
    onError: (error) => {
      const latest = conflictCurrent<KnowledgeTopic>(error)
      latest ? setConflict(latest) : setMessage(error instanceof Error ? error.message : '儲存失敗。')
    },
  })

  const savePurpose = useMutation({
    mutationFn: () => purposeEdit
      ? api.updateEntity<KnowledgePurpose>('knowledge-purpose', purposeEdit.id, purposeEdit.version, { text: purposeText })
      : api.createEntity<KnowledgePurpose>('knowledge-purpose', { text: purposeText }),
    onSuccess: async () => {
      setPurposeEdit(null)
      setPurposeText('')
      setPurposeOpen(false)
      await refresh()
    },
    onError: (error) => setMessage(error instanceof Error ? error.message : '儲存失敗。'),
  })

  const close = () => {
    setOpen(false)
    setEditing(null)
    setForm(emptyTopic)
    setConflict(null)
  }

  const edit = (row: KnowledgeTopic) => {
    setEditing(row)
    setForm(toForm(row))
    setOpen(true)
  }

  const openNewForCategory = (categoryName?: string) => {
    setEditing(null)
    setForm({
      ...emptyTopic,
      category: categoryName || (groups[0] || defaultCategory),
    })
    setOpen(true)
  }

  const remove = async (entity: 'knowledge-purpose' | 'knowledge-topics', row: KnowledgePurpose | KnowledgeTopic, title: string) => {
    if (!window.confirm(`確定刪除「${title}」？`)) return
    try {
      await api.deleteEntity(entity, row.id, row.version)
      if (entity === 'knowledge-topics') {
        queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
          if (!old) return old
          return {
            ...old,
            knowledge: {
              ...old.knowledge,
              topics: old.knowledge.topics.filter((t) => t.id !== row.id),
            },
          }
        })
      }
      setMessage(`「${title}」已刪除。`)
      await refresh()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '刪除失敗。')
    }
  }

  const dropOnTopic = async (event: React.DragEvent, target: KnowledgeTopic) => {
    event.preventDefault()
    event.stopPropagation()
    const currentDragId = draggingId
    setDraggingId(null)
    setDragOverTopicId(null)
    setDragOverGroup(null)

    const dragged = data.knowledge.topics.find((row) => row.id === currentDragId)
    if (!dragged || dragged.id === target.id) return

    const targetCategory = target.category
    const categoryTopics = data.knowledge.topics
      .filter((row) => row.category === targetCategory && row.id !== dragged.id)
      .sort((a, b) => a.sortOrder - b.sortOrder)

    const targetIndex = categoryTopics.findIndex((row) => row.id === target.id)
    const sortOrder = sortOrderBefore(categoryTopics, targetIndex)

    const previousTopics = data.knowledge.topics
    queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
      if (!old) return old
      const updatedList = old.knowledge.topics
        .map((t) => (t.id === dragged.id ? { ...t, category: targetCategory, sortOrder } : t))
        .sort((a, b) => a.sortOrder - b.sortOrder)
      return {
        ...old,
        knowledge: {
          ...old.knowledge,
          topics: updatedList,
        },
      }
    })

    try {
      const payload: Partial<KnowledgeTopic> = { sortOrder }
      if (dragged.category !== targetCategory) {
        payload.category = targetCategory
      }
      const updated = await api.updateEntity<KnowledgeTopic>('knowledge-topics', dragged.id, dragged.version, payload)
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        return {
          ...old,
          knowledge: {
            ...old.knowledge,
            topics: old.knowledge.topics.map((t) => (t.id === updated.id ? updated : t)),
          },
        }
      })
      if (dragged.category !== targetCategory) {
        setMessage(`已將「${dragged.topic}」移動至「${targetCategory}」。`)
      } else {
        setMessage('排序已更新。')
      }
    } catch (error) {
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        return {
          ...old,
          knowledge: { ...old.knowledge, topics: previousTopics },
        }
      })
      const latest = conflictCurrent<KnowledgeTopic>(error)
      latest ? setConflict(latest) : setMessage(error instanceof Error ? error.message : '更新排序失敗。')
    }
  }

  const dropOnGroup = async (event: React.DragEvent, targetCategory: string) => {
    event.preventDefault()
    const currentDragId = draggingId
    setDraggingId(null)
    setDragOverTopicId(null)
    setDragOverGroup(null)

    const dragged = data.knowledge.topics.find((row) => row.id === currentDragId)
    if (!dragged) return
    if (dragged.category === targetCategory) return

    const categoryTopics = data.knowledge.topics
      .filter((row) => row.category === targetCategory && row.id !== dragged.id)
      .sort((a, b) => a.sortOrder - b.sortOrder)

    const lastTopic = categoryTopics[categoryTopics.length - 1]
    const sortOrder = lastTopic ? lastTopic.sortOrder + 1000 : 1000

    const previousTopics = data.knowledge.topics
    queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
      if (!old) return old
      const updatedList = old.knowledge.topics
        .map((t) => (t.id === dragged.id ? { ...t, category: targetCategory, sortOrder } : t))
        .sort((a, b) => a.sortOrder - b.sortOrder)
      return {
        ...old,
        knowledge: {
          ...old.knowledge,
          topics: updatedList,
        },
      }
    })

    try {
      const updated = await api.updateEntity<KnowledgeTopic>('knowledge-topics', dragged.id, dragged.version, {
        category: targetCategory,
        sortOrder,
      })
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        return {
          ...old,
          knowledge: {
            ...old.knowledge,
            topics: old.knowledge.topics.map((t) => (t.id === updated.id ? updated : t)),
          },
        }
      })
      setMessage(`已將「${dragged.topic}」移動至「${targetCategory}」。`)
    } catch (error) {
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        return {
          ...old,
          knowledge: { ...old.knowledge, topics: previousTopics },
        }
      })
      const latest = conflictCurrent<KnowledgeTopic>(error)
      latest ? setConflict(latest) : setMessage(error instanceof Error ? error.message : '移動分類失敗。')
    }
  }

  return (
    <section className="page-stack">
      <div className="working-header-bar">
        <div className="working-header-title-group">
          <h1>Know-how</h1>
          <div className="working-stat-pills">
            <span className="stat-pill total">全部 <strong>{data.knowledge.topics.length}</strong></span>
            <span className="stat-pill green">已完成 <strong>{data.knowledge.topics.filter((row) => row.done).length}</strong></span>
            <span className="stat-pill blue">完成率 <strong>{data.knowledge.topics.length ? `${Math.round((data.knowledge.topics.filter((row) => row.done).length / data.knowledge.topics.length) * 100)}%` : '—'}</strong></span>
            <span className="stat-pill amber">分類 <strong>{new Set(data.knowledge.topics.map((row) => row.category)).size}</strong></span>
          </div>
        </div>
        {canEdit ? (
          <Button variant="primary" onClick={() => openNewForCategory()}>
            <Plus size={16} weight="bold" />新增 Topic
          </Button>
        ) : null}
      </div>

      {message ? (
        <div className="floating-toast">
          <span>{message}</span>
          <button type="button" onClick={() => setMessage('')} aria-label="關閉通知">×</button>
        </div>
      ) : null}

      {data.knowledge.purpose.length > 0 ? (
        <div className="purpose-section-wrap">
          <div className="purpose-strip">
            <div className="purpose-strip-content">
              <span className="purpose-strip-label">
                <Target size={15} weight="bold" /> 學習目的
              </span>
              {!purposeExpanded ? (
                <div className="purpose-strip-items">
                  {data.knowledge.purpose.map((item, idx) => (
                    <span key={item.id} className="purpose-strip-item" title={item.text}>
                      <strong>{idx + 1}.</strong> {item.text}
                    </span>
                  ))}
                </div>
              ) : (
                <span style={{ fontSize: '11.5px', color: '#64748b' }}>
                  維持團隊對知識承接結果的共同定義。
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {purposeExpanded && canEdit ? (
                <Button
                  onClick={() => {
                    setPurposeEdit(null)
                    setPurposeText('')
                    setPurposeOpen(true)
                  }}
                >
                  <Plus size={14} weight="bold" /> 新增目的
                </Button>
              ) : null}
              <button
                type="button"
                className="purpose-strip-toggle"
                onClick={() => setPurposeExpanded((prev) => !prev)}
              >
                <span>{purposeExpanded ? '收合' : '管理目的'}</span>
                {purposeExpanded ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
              </button>
            </div>
          </div>

          {purposeExpanded ? (
            <div className="purpose-manage-box">
              <div className="purpose-grid">
                {data.knowledge.purpose.map((row, index) => (
                  <article key={row.id} className="purpose-card">
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <p>{row.text}</p>
                    {canEdit ? (
                      <button
                        type="button"
                        className="text-button"
                        onClick={() => {
                          setPurposeEdit(row)
                          setPurposeText(row.text)
                          setPurposeOpen(true)
                        }}
                      >
                        編輯
                      </button>
                    ) : null}
                    {isAdmin ? (
                      <button
                        type="button"
                        className="icon-button danger"
                        aria-label="刪除目的"
                        onClick={() => void remove('knowledge-purpose', row, row.text)}
                      >
                        <Trash size={16} />
                      </button>
                    ) : null}
                  </article>
                ))}
              </div>
              {canEdit && purposeOpen ? (
                <form
                  className="purpose-editor"
                  style={{ marginTop: '12px', marginInline: 0 }}
                  onSubmit={(event) => {
                    event.preventDefault()
                    savePurpose.mutate()
                  }}
                >
                  <Field label={purposeEdit ? '編輯學習目的' : '新增學習目的'}>
                    <textarea
                      rows={3}
                      value={purposeText}
                      onChange={(e) => setPurposeText(e.target.value)}
                      required
                    />
                  </Field>
                  <div>
                    <Button
                      onClick={() => {
                        setPurposeEdit(null)
                        setPurposeText('')
                        setPurposeOpen(false)
                      }}
                      type="button"
                    >
                      取消
                    </Button>
                    <Button variant="primary" type="submit" disabled={savePurpose.isPending}>
                      儲存
                    </Button>
                  </div>
                </form>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <Panel>
        <div className="panel-toolbar">
          <div>
            <h2>Knowledge Map</h2>
            <p>按分類檢視報告主題與負責人。</p>
          </div>
          <div className="filters">
            <input
              className="search-input"
              placeholder="搜尋主題、分類或報告人"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        <div className="knowledge-columns">
          {groups.map((group) => (
            <section
              key={group}
              className={`knowledge-group ${dragOverGroup === group ? 'group-drag-over' : ''}`}
              onDragOver={(e) => {
                if (canEdit && draggingId) {
                  e.preventDefault()
                  if (dragOverGroup !== group) setDragOverGroup(group)
                }
              }}
              onDragLeave={(e) => {
                if (e.currentTarget === e.target) setDragOverGroup(null)
              }}
              onDrop={(e) => canEdit && void dropOnGroup(e, group)}
            >
              <header>
                <div className="knowledge-group-title">
                  <BookOpenText size={21} weight="duotone" />
                  <div>
                    <strong>{group}</strong>
                    <small>{topics.filter((row) => row.category === group).length} Topics</small>
                  </div>
                </div>
                {canEdit ? (
                  <button
                    type="button"
                    className="icon-button"
                    title={`在「${group}」新增 Topic`}
                    aria-label={`在「${group}」新增 Topic`}
                    onClick={(e) => {
                      e.stopPropagation()
                      openNewForCategory(group)
                    }}
                  >
                    <Plus size={16} weight="bold" />
                  </button>
                ) : null}
              </header>
              <div className={`knowledge-group-body ${dragOverGroup === group ? 'drag-over' : ''}`}>
                {topics.filter((row) => row.category === group).map((row) => (
                  <article
                    key={row.id}
                    className={`topic-card ${draggingId === row.id ? 'dragging' : ''} ${dragOverTopicId === row.id ? 'drag-over' : ''}`}
                    draggable={canEdit}
                    onDragStart={(e) => {
                      if (!canEdit) return
                      setDraggingId(row.id)
                      e.dataTransfer.setData('text/plain', row.id)
                    }}
                    onDragEnd={() => {
                      setDraggingId(null)
                      setDragOverTopicId(null)
                      setDragOverGroup(null)
                    }}
                    onDragOver={(e) => {
                      if (canEdit && draggingId && draggingId !== row.id) {
                        e.preventDefault()
                        e.stopPropagation()
                        if (dragOverTopicId !== row.id) {
                          setDragOverTopicId(row.id)
                        }
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverTopicId === row.id) {
                        setDragOverTopicId(null)
                      }
                    }}
                    onDrop={(e) => canEdit && void dropOnTopic(e, row)}
                    onClick={() => canEdit && edit(row)}
                  >
                    {canEdit ? (
                      <div
                        className="topic-drag-handle"
                        title="拖曳可排序或跨欄位移動"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DotsSixVertical size={18} weight="bold" />
                      </div>
                    ) : null}
                    <div className={row.done ? 'topic-check done' : 'topic-check'}>
                      {row.done ? <CheckCircle size={21} weight="fill" /> : <Circle size={21} />}
                    </div>
                    <div className="topic-content">
                      <div className="topic-title-line">
                        {row.groupName && row.groupName.trim() ? (
                          <CategoryBadge value={row.groupName.trim()} />
                        ) : null}
                        <strong>{row.topic}</strong>
                        {row.reportDate ? <span className="topic-date">{row.reportDate}</span> : null}
                      </div>
                      {row.reporter && row.reporter.trim() !== '尚未指派' ? (
                        <small>Reporter · {row.reporter}</small>
                      ) : null}
                      {row.links && row.links.length > 0 ? (
                        <div className="topic-links-wrap">
                          {row.links.map((link) => (
                            <a
                              key={link.id}
                              href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                              target="_blank"
                              rel="noreferrer"
                              className="topic-link-badge"
                              title={link.url}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <ArrowSquareOut size={12} weight="bold" />
                              <span>{link.title || link.url}</span>
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    {isAdmin ? (
                      <button
                        type="button"
                        className="icon-button danger"
                        aria-label="刪除"
                        onClick={(event) => {
                          event.stopPropagation()
                          void remove('knowledge-topics', row, row.topic)
                        }}
                      >
                        <Trash size={16} />
                      </button>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
        {topics.length === 0 ? <div className="table-empty">找不到符合條件的知識主題。</div> : null}
      </Panel>

      <Drawer
        open={open}
        title={editing ? '編輯 Know-how Topic' : '新增 Know-how Topic'}
        subtitle={editing ? `版本 ${editing.version}${editing.updatedByName && !editing.updatedByName.includes('Legacy') ? ` · ${editing.updatedByName}` : ''}` : '建立一個可追蹤的知識主題'}
        onClose={close}
        footer={(
          <>
            <Button onClick={close}>取消</Button>
            <Button variant="primary" type="submit" form="topic-form" disabled={save.isPending}>
              {save.isPending ? '儲存中…' : '儲存'}
            </Button>
          </>
        )}
      >
        <TopicFormView
          form={form}
          setForm={setForm}
          categories={groups}
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        />
      </Drawer>

      <ConflictDialog
        open={Boolean(conflict)}
        local={form as unknown as Record<string, unknown>}
        remote={(conflict ?? {}) as unknown as Record<string, unknown>}
        labels={topicLabels}
        onClose={() => setConflict(null)}
        onUseLatest={() => {
          if (conflict) {
            setEditing(conflict)
            setForm(toForm(conflict))
            setConflict(null)
          }
        }}
      />
    </section>
  )
}

function TopicFormView({
  form,
  setForm,
  categories,
  onSubmit,
}: {
  form: TopicForm
  setForm: (value: TopicForm) => void
  categories: string[]
  onSubmit: (event: FormEvent) => void
}) {
  const [newLinkTitle, setNewLinkTitle] = useState('')
  const [newLinkUrl, setNewLinkUrl] = useState('')

  const handleAddLink = () => {
    const trimmedUrl = newLinkUrl.trim()
    if (!trimmedUrl) return
    const item: WorkingLink = {
      id: crypto.randomUUID(),
      title: newLinkTitle.trim() || trimmedUrl,
      url: trimmedUrl,
    }
    setForm({ ...form, links: [...(form.links || []), item] })
    setNewLinkTitle('')
    setNewLinkUrl('')
  }

  const selectedReporters = form.reporter
    ? form.reporter.split(/[,/]/).map((item) => item.trim()).filter(Boolean)
    : []

  const toggleReporter = (name: string) => {
    const next = selectedReporters.includes(name)
      ? selectedReporters.filter((v) => v !== name)
      : [...selectedReporters, name]
    setForm({ ...form, reporter: next.join(', ') })
  }

  return (
    <form id="topic-form" className="form-grid two" onSubmit={onSubmit}>
      <Field label="Category (知識分類)">
        <input
          list="category-options"
          required
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          placeholder="選擇現有分類或直接輸入新分類"
        />
        <datalist id="category-options">
          {categories.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
      </Field>
      <Field label="Group (小組/模組，可選)">
        <input
          value={form.groupName}
          onChange={(e) => setForm({ ...form, groupName: e.target.value })}
          placeholder="例如 ESS, FW Team Work 等，無則留空"
        />
      </Field>
      <Field label="Topic (主題名稱)">
        <textarea
          required
          rows={3}
          value={form.topic}
          onChange={(e) => setForm({ ...form, topic: e.target.value })}
          placeholder="輸入主題名稱"
        />
      </Field>
      <Field label="Reporter (報告人)">
        <div className="owner-selector" role="group" aria-label="選擇報告人">
          {workingItemOwners.map((owner) => {
            const selected = selectedReporters.includes(owner)
            return (
              <button
                key={owner}
                type="button"
                className={selected ? 'selected' : ''}
                aria-pressed={selected}
                onClick={() => toggleReporter(owner)}
              >
                {selected ? '✓' : '+'} {owner}
              </button>
            )
          })}
        </div>
        <input
          aria-label="報告人"
          value={form.reporter}
          onChange={(e) => setForm({ ...form, reporter: e.target.value })}
          placeholder="點選上方快選成員，或直接輸入姓名"
        />
      </Field>
      <Field label="Report Date (預定報告日期)">
        <input
          value={form.reportDate}
          onChange={(e) => setForm({ ...form, reportDate: e.target.value })}
          placeholder="例如 9/25 或 2026-09-25"
        />
      </Field>
      <Field label="Completion (狀態)">
        <label className="check-control">
          <input type="checkbox" checked={form.done} onChange={(e) => setForm({ ...form, done: e.target.checked })} />
          主題已完成
        </label>
      </Field>

      <div className="drawer-links-section">
        <div className="drawer-section-title">
          <span>參考連結 / 網址 ({form.links?.length || 0})</span>
        </div>
        {form.links && form.links.length > 0 ? (
          <div>
            {form.links.map((link) => (
              <div key={link.id} className="link-drawer-row">
                <input
                  type="text"
                  placeholder="標題 (例: 規格書、簡報)"
                  className="link-title-input"
                  value={link.title}
                  onChange={(e) => {
                    const list = (form.links || []).map((l) => (l.id === link.id ? { ...l, title: e.target.value } : l))
                    setForm({ ...form, links: list })
                  }}
                />
                <input
                  type="text"
                  placeholder="URL 網址 (https://...)"
                  className="link-url-input"
                  value={link.url}
                  onChange={(e) => {
                    const list = (form.links || []).map((l) => (l.id === link.id ? { ...l, url: e.target.value } : l))
                    setForm({ ...form, links: list })
                  }}
                />
                {link.url ? (
                  <a
                    href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="icon-button"
                    title="開啟連結測試"
                    style={{ display: 'inline-grid', placeItems: 'center', width: '28px', height: '28px' }}
                  >
                    <ArrowSquareOut size={16} />
                  </a>
                ) : null}
                <button
                  type="button"
                  title="刪除此連結"
                  onClick={() => {
                    setForm({ ...form, links: (form.links || []).filter((l) => l.id !== link.id) })
                  }}
                >
                  <Trash size={15} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: '#8c98a5', fontSize: '12px', marginBottom: '8px' }}>
            尚未建立連結。可於下方新增相關文件、簡報、規範等連結。
          </div>
        )}
        <div className="add-link-bar">
          <input
            type="text"
            placeholder="連結名稱 (例: 架構簡報、Spec)"
            value={newLinkTitle}
            onChange={(e) => setNewLinkTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddLink()
              }
            }}
          />
          <input
            type="text"
            placeholder="https://..."
            value={newLinkUrl}
            onChange={(e) => setNewLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleAddLink()
              }
            }}
          />
          <Button type="button" onClick={handleAddLink}>
            <Plus size={14} weight="bold" /> 新增連結
          </Button>
        </div>
      </div>
    </form>
  )
}

function toForm(row: KnowledgeTopic): TopicForm {
  const { category, groupName, topic, done, reporter, reportDate, links } = row
  return { category, groupName, topic, done, reporter, reportDate, links: Array.isArray(links) ? links : [] }
}
